
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

export interface AdminProfile {
  id: string;
  userId: string;
  adminLevel: string;
  isActive: boolean;
  createdAt: string;
}

export const useAdminAuth = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminProfile, setAdminProfile] = useState<AdminProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { user, session } = useAuth();

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user || !session) {
        setIsAdmin(false);
        setAdminProfile(null);
        setIsLoading(false);
        return;
      }

      try {
        // Check if user is admin
        const { data: isAdminResult, error: adminCheckError } = await supabase.rpc('is_admin', { 
          user_uuid: user.id 
        });

        if (adminCheckError) {
          console.error('Error checking admin status:', adminCheckError);
          setIsLoading(false);
          return;
        }

        setIsAdmin(isAdminResult || false);

        // If admin, fetch admin profile
        if (isAdminResult) {
          const { data: adminData, error: profileError } = await supabase
            .from('admin_users')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_active', true)
            .maybeSingle();

          if (!profileError && adminData) {
            setAdminProfile({
              id: adminData.id,
              userId: adminData.user_id,
              adminLevel: adminData.admin_level,
              isActive: adminData.is_active,
              createdAt: adminData.created_at
            });
          }
        }
      } catch (error) {
        console.error('Error in admin auth check:', error);
      }
      
      setIsLoading(false);
    };

    checkAdminStatus();
  }, [user, session]);

  const logAdminActivity = async (action: string, details?: any) => {
    if (!user || !isAdmin) return;

    try {
      await supabase.rpc('log_admin_activity', {
        p_admin_user_id: user.id,
        p_action: action,
        p_details: details ? JSON.stringify(details) : null
      });
    } catch (error) {
      console.warn('Could not log admin activity:', error);
    }
  };

  return {
    isAdmin,
    adminProfile,
    isLoading,
    logAdminActivity
  };
};
